import type { Activity, Transition, Workflow, WorkflowBounds, SelectedComponent } from './types'
import { TRANSITION_HIT_DISTANCE, EDGE_DRAG_THRESHOLD } from './constants'

/** Distance from point (px,py) to the line segment (x1,y1)-(x2,y2). */
export function distanceToLineSegment(
  px: number, py: number,
  x1: number, y1: number,
  x2: number, y2: number,
): number {
  const A = px - x1
  const B = py - y1
  const C = x2 - x1
  const D = y2 - y1

  const dot = A * C + B * D
  const lenSq = C * C + D * D
  let param = -1
  if (lenSq !== 0) param = dot / lenSq

  let xx: number, yy: number
  if (param < 0) {
    xx = x1; yy = y1
  } else if (param > 1) {
    xx = x2; yy = y2
  } else {
    xx = x1 + param * C
    yy = y1 + param * D
  }

  const dx = px - xx
  const dy = py - yy
  return Math.sqrt(dx * dx + dy * dy)
}

/** Connection points: transition exits from right edge, enters left edge. */
export function getConnectionPoints(from: Activity, to: Activity) {
  return {
    startX: from.x + getActivityWidth(from),
    startY: from.y + from.height / 2,
    endX: to.x,
    endY: to.y + to.height / 2,
  }
}

/**
 * Build an SVG path `d` attribute for a transition line.
 * Uses a quadratic curve when there are multiple outgoing transitions from the same source.
 */
export function getTransitionPath(
  transition: Transition,
  workflow: Workflow,
): string | null {
  const from = workflow.activities.find(a => a.id === transition.from)
  const to = workflow.activities.find(a => a.id === transition.to)
  if (!from || !to) return null

  const { startX, startY, endX, endY } = getConnectionPoints(from, to)

  const outgoing = workflow.transitions.filter(t => t.from === from.id)
  if (outgoing.length > 1) {
    const idx = outgoing.indexOf(transition)
    const midX = (startX + endX) / 2
    const offset = (idx - (outgoing.length - 1) / 2) * 30
    const controlY = (startY + endY) / 2 + offset
    return `M ${startX} ${startY} Q ${midX} ${controlY} ${endX} ${endY}`
  }

  return `M ${startX} ${startY} L ${endX} ${endY}`
}

/** Arrowhead points as a polygon string, placed at the end of the line. */
export function getArrowheadPoints(
  endX: number, endY: number,
  startX: number, startY: number,
): string {
  const angle = Math.atan2(endY - startY, endX - startX)
  const cos = Math.cos(angle)
  const sin = Math.sin(angle)

  // Arrow tip at (endX, endY), two base points 10px back, 5px apart
  const x1 = endX - 10 * cos + 5 * sin
  const y1 = endY - 10 * sin - 5 * cos
  const x2 = endX - 10 * cos - 5 * sin
  const y2 = endY - 10 * sin + 5 * cos

  return `${endX},${endY} ${x1},${y1} ${x2},${y2}`
}

/** Bounding box of all activities. */
export function getWorkflowBounds(workflow: Workflow): WorkflowBounds | null {
  if (workflow.activities.length === 0) return null

  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity

  for (const a of workflow.activities) {
    const aw = getActivityWidth(a)
    minX = Math.min(minX, a.x)
    minY = Math.min(minY, a.y)
    maxX = Math.max(maxX, a.x + aw)
    maxY = Math.max(maxY, a.y + a.height)
  }

  return { x: minX, y: minY, width: maxX - minX, height: maxY - minY }
}

/** Minimum distance from point (px,py) to a quadratic Bezier curve P0→P1→P2, sampled. */
function distanceToQuadraticBezier(
  px: number, py: number,
  x0: number, y0: number,
  cx: number, cy: number,
  x2: number, y2: number,
): number {
  let minDist = Infinity
  const steps = 20
  for (let i = 0; i <= steps; i++) {
    const t = i / steps
    const mt = 1 - t
    const bx = mt * mt * x0 + 2 * mt * t * cx + t * t * x2
    const by = mt * mt * y0 + 2 * mt * t * cy + t * t * y2
    const dx = px - bx
    const dy = py - by
    const d = Math.sqrt(dx * dx + dy * dy)
    if (d < minDist) minDist = d
  }
  return minDist
}

/** Distance from a point to the actual rendered transition path (line or curve). */
function distanceToTransitionPath(
  px: number, py: number,
  transition: Transition,
  workflow: Workflow,
): number {
  const from = workflow.activities.find(a => a.id === transition.from)
  const to = workflow.activities.find(a => a.id === transition.to)
  if (!from || !to) return Infinity

  const { startX, startY, endX, endY } = getConnectionPoints(from, to)

  const outgoing = workflow.transitions.filter(t => t.from === from.id)
  if (outgoing.length > 1) {
    const idx = outgoing.indexOf(transition)
    const midX = (startX + endX) / 2
    const offset = (idx - (outgoing.length - 1) / 2) * 30
    const controlY = (startY + endY) / 2 + offset
    return distanceToQuadraticBezier(px, py, startX, startY, midX, controlY, endX, endY)
  }

  return distanceToLineSegment(px, py, startX, startY, endX, endY)
}

/** Find the component (activity or transition) at a canvas position. */
export function findComponentAtPosition(
  x: number, y: number,
  workflow: Workflow,
): SelectedComponent {
  // Check activities first (they should take priority over transitions)
  for (let i = workflow.activities.length - 1; i >= 0; i--) {
    const a = workflow.activities[i]
    const aw = getActivityWidth(a)
    if (x >= a.x && x <= a.x + aw && y >= a.y && y <= a.y + a.height) {
      return a
    }
  }

  // Then check transitions using actual path geometry
  let closestTransition: Transition | null = null
  let minDist = Infinity

  for (const t of workflow.transitions) {
    const d = distanceToTransitionPath(x, y, t, workflow)
    if (d < TRANSITION_HIT_DISTANCE && d < minDist) {
      minDist = d
      closestTransition = t
    }
  }

  if (closestTransition) return closestTransition

  return null
}

/** Check if a component is a transition (has from/to). */
export function isTransition(component: SelectedComponent): component is Transition {
  if (!component) return false
  return 'from' in component && 'to' in component
}

/** Check if a component is an activity. */
export function isActivity(component: SelectedComponent): component is Activity {
  if (!component) return false
  return 'type' in component && !('from' in component)
}

/** Compute the rendered width of an activity (matches ActivityNode logic). */
export function getActivityWidth(activity: Activity): number {
  const iconSpace = activity.icon ? 20 : 0
  return Math.max(activity.width, activity.description.length * 8 + 20 + iconSpace)
}

/** Check if a point is on the edge (border zone) of an activity, for edge-drag transition creation. */
export function isOnActivityEdge(x: number, y: number, activity: Activity): boolean {
  const ax = activity.x
  const ay = activity.y
  const aw = getActivityWidth(activity)
  const ah = activity.height
  const t = EDGE_DRAG_THRESHOLD

  // Must be within the outer boundary
  if (x < ax - t || x > ax + aw + t || y < ay - t || y > ay + ah + t) return false
  // Must NOT be fully inside the inner area
  if (x > ax + t && x < ax + aw - t && y > ay + t && y < ay + ah - t) return false
  return true
}

/** Find an activity whose body (full rect) contains the point. */
export function findActivityAtPosition(x: number, y: number, workflow: Workflow): Activity | null {
  for (let i = workflow.activities.length - 1; i >= 0; i--) {
    const a = workflow.activities[i]
    const aw = getActivityWidth(a)
    if (x >= a.x && x <= a.x + aw && y >= a.y && y <= a.y + a.height) return a
  }
  return null
}

/** Midpoint of a transition for label placement. */
export function getTransitionMidpoint(transition: Transition, workflow: Workflow) {
  const from = workflow.activities.find(a => a.id === transition.from)
  const to = workflow.activities.find(a => a.id === transition.to)
  if (!from || !to) return { x: 0, y: 0 }

  const pts = getConnectionPoints(from, to)
  return {
    x: (pts.startX + pts.endX) / 2,
    y: (pts.startY + pts.endY) / 2 - 10,
  }
}
